
import { LoadingType } from "components/Formulario/Components/PortalSelect";

// --- Funções de Geração de Configuração ---

/**
 * Cria o objeto de configuração para um divisor.
 * @param {string} text - O texto a ser exibido no divisor.
 * @param {'before' | 'after'} type - Posição do divisor em relação ao input.
 * @returns {object} O objeto de configuração do divisor.
 */
export const createDividerConfig = (text = '', type = 'before') => ({
    [type]: {
        show: true,
        text: text,
    },
});

export const createTableContainerWithCustomForm = (tituloBotao, formCustomComponent, data, containerType = "modal") => ({
    tituloBotao,
    containerSettings:{
        containerType,
        content: {
            contentType: "customForm",
            props: {
                data,
            },
            formCustomComponent
        }
    }
})

export const createTableContainerWithTable = (tituloBotao, columns, adicionarDados, dataSource = [],containerType = "modal") => ({
    tituloBotao,
    containerSettings:{
        containerType,
        content:{
            contentType: "table",
            columns,
            adicionarDados,
            dataSource
        }
    }
})

export const createPortalTable = (campoRegistrosDaListagem,campoIdDaListagem,placeholderFiltro, callback) => ({
    callback,
    campoRegistrosDaListagem,
    campoIdDaListagem,
    placeholderFiltro
})

/**
 * Atualiza o status de validação e a mensagem de ajuda de um input específico em um array de inputs.
 * Retorna um novo array de inputs com a modificação aplicada.
 * @param {Array<object>} inputsArray - O array atual de configurações de inputs.
 * @param {string} fieldName - O nome do campo a ser atualizado.
 * @param {string} status - O status de validação (e.g., 'success', 'error', 'validating', '').
 * @param {string} message - A mensagem de ajuda a ser exibida.
 * @returns {Array<object>} Um novo array de inputs com o campo atualizado.
 */
export const updateInputStatus = (inputsArray, fieldName, status, message) => {
    const novosInputs = [...inputsArray];
    const index = novosInputs.findIndex(i => i.input.name === fieldName);
    if (index !== -1) {
        novosInputs[index] = {
            ...novosInputs[index],
            input: {
                ...novosInputs[index].input,
                validateStatus: status,
                help: message,
            }
        };
    }
    return novosInputs;
};


// --- Funções de Geração de Input ---

const createBaseInput = (type, name, label, { required = true, rules = [], hidden = false, ...inputProps } = {}, otherProps = {}, divider = null) => {
    const finalRules = [...rules];
    if (required && !finalRules.some(rule => rule.required)) {
        const message = label ? `O campo ${label} é obrigatório` : `Esta opção é obrigatória.`;
        finalRules.unshift({ required: true, message });
    }

    const config = {
        input: {
            type,
            name,
            label,
            rules: finalRules,
            hidden,
            colSpan: 24,
            ...inputProps,
        },
        ...otherProps
    };

    if (divider) {
        config.divider = divider;
    }

    return config;
};

export const createTextInput = (name, label, inputProps, otherProps, divider) => {
    return createBaseInput('text', name, label, inputProps, otherProps, divider);
};

export const createTextAreaInput = (name, label, inputProps, otherProps, divider) => {
    return createBaseInput('textarea', name, label, inputProps, otherProps, divider);
};
export const createTextSufixoInput = (name, label, inputProps, otherProps, divider) => {
    return createBaseInput('textSufixos', name, label, inputProps, otherProps, divider);
};
export const createPhoneInput = (name, label, inputProps, otherProps, divider) => {
    return createBaseInput('phone', name, label, inputProps, otherProps, divider);
};

export const createSelectInput = (name, label, options, inputProps, otherProps, divider) => {
    const defaultOptions = {
        idField: "id",
        labelField: "label",
        loadingType: LoadingType.AoRenderizar,
        autoFillWhenSingleValue: true,
        callback: async () => [],
    };
    return createBaseInput('select', name, label, { options: { ...defaultOptions, ...options }, ...inputProps }, otherProps, divider);
};

export const createSelectGroupInput = (name, label, options, inputProps, otherProps, divider) => {
    const defaultOptions = {
        idField: "id",
        labelField: "label",
        loadingType: LoadingType.AoRenderizar,
        autoFillWhenSingleValue: true,
        callback: async () => [],
    };
    return createBaseInput('selectGroup', name, label, { options: { ...defaultOptions, ...options }, ...inputProps }, otherProps, divider);
};

export const createHiddenInput = (name, inputProps) => {
    return createBaseInput('text', name, '', { ...inputProps, hidden: true });
};

export const createAnexoInput = (name, label, inputProps, otherProps, divider) => {
    return createBaseInput('anexo', name, label, inputProps, otherProps, divider);
};

export const createDatePickerInput = (name, label, inputProps, otherProps, divider) => {
    return createBaseInput('date', name, label, inputProps, otherProps, divider);
};

export const createRadioInput = (name, label, options, inputProps, otherProps, divider) => {
    return createBaseInput('radio', name, label, { options, ...inputProps }, otherProps, divider);
};

export const createCheckboxInput = (name, texto, inputProps, otherProps, divider) => {
    return createBaseInput('checkbox', name, '', { texto, ...inputProps }, otherProps, divider);
};

export const createCheckboxGroupInput = (name, label, options, inputProps, otherProps, divider) => {
    return createBaseInput('checkboxGroup', name, label, { options, ...inputProps }, otherProps, divider);
};

export const createTableInput = (name, label, columns, container, inputProps, otherProps, divider, collapse = {}) => {
    const { dataSource, ...restOtherProps } = otherProps || {};
    const baseConfig = createBaseInput('table', name, label, inputProps, restOtherProps, divider);
    baseConfig.columns = columns;
    baseConfig.container = container;
    baseConfig.collapse = !!Object.keys(collapse).length ? collapse : null;
    if (dataSource) {
        baseConfig.dataSource = dataSource;
    }

    return baseConfig;
};

